import type { Point, SlateElement } from '../scene/types';
import { isLinear } from '../scene/types';

export type Vec = { readonly x: number; readonly y: number };

export function vec(x: number, y: number): Vec {
  return { x, y };
}

export function add(a: Vec, b: Vec): Vec {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec, b: Vec): Vec {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(a: Vec, k: number): Vec {
  return { x: a.x * k, y: a.y * k };
}

export function length(a: Vec): number {
  return Math.hypot(a.x, a.y);
}

/** Unit vector, or (0,0) when the input has no length. Callers must handle zero. */
export function normalize(a: Vec): Vec {
  const l = Math.hypot(a.x, a.y);
  return l === 0 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
}

export function rotateVec(a: Vec, angle: number): Vec {
  if (angle === 0) return a;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
}

export function centerOf(el: SlateElement): Vec {
  return { x: el.x + el.width / 2, y: el.y + el.height / 2 };
}

/**
 * World point -> element-local point. Local space has its origin at the
 * element's unrotated top-left, so (0,0)..(width,height) is the box.
 * This is the ONLY place rotation is undone; nothing else should touch `angle`.
 */
export function toLocal(p: Vec, el: SlateElement): Vec {
  const c = centerOf(el);
  const r = rotateVec(sub(p, c), -el.angle);
  return { x: r.x + el.width / 2, y: r.y + el.height / 2 };
}

/** Element-local point -> world point. Exact inverse of toLocal. */
export function toWorld(q: Vec, el: SlateElement): Vec {
  const c = centerOf(el);
  const r = rotateVec({ x: q.x - el.width / 2, y: q.y - el.height / 2 }, el.angle);
  return add(r, c);
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export function rectFromPoints(a: Vec, b: Vec): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x <= b.x + b.width &&
    b.x <= a.x + a.width &&
    a.y <= b.y + b.height &&
    b.y <= a.y + a.height
  );
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

export function unionRects(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function expandRect(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, width: r.width + by * 2, height: r.height + by * 2 };
}

/**
 * Axis-aligned world bounds that fully contain a possibly-rotated element,
 * inflated by half the stroke so culling never clips a visible edge.
 */
export function worldBounds(el: SlateElement): Rect {
  const corners: Vec[] = [
    toWorld({ x: 0, y: 0 }, el),
    toWorld({ x: el.width, y: 0 }, el),
    toWorld({ x: el.width, y: el.height }, el),
    toWorld({ x: 0, y: el.height }, el),
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of corners) {
    minX = Math.min(minX, c.x);
    minY = Math.min(minY, c.y);
    maxX = Math.max(maxX, c.x);
    maxY = Math.max(maxY, c.y);
  }
  const pad = el.strokeWidth / 2 + 1;
  return { x: minX - pad, y: minY - pad, width: maxX - minX + pad * 2, height: maxY - minY + pad * 2 };
}

/** Bounding box of local points, used to re-derive width/height for linear elements. */
export function pointsBounds(points: readonly Point[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [px, py] of points) {
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** World position of a linear element's nth point. */
export function linearPointToWorld(el: SlateElement, index: number): Vec {
  if (!isLinear(el)) return centerOf(el);
  const p = el.points[index];
  if (!p) return centerOf(el);
  return toWorld({ x: p[0], y: p[1] }, el);
}

/** Squared distance from p to segment ab. Squared to avoid a sqrt per segment. */
export function distanceToSegmentSq(p: Vec, a: Vec, b: Vec): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = p.x - a.x;
    const ey = p.y - a.y;
    return ex * ex + ey * ey;
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  const ex = p.x - cx;
  const ey = p.y - cy;
  return ex * ex + ey * ey;
}

/**
 * Intersection of segments p1p2 and p3p4, or null when they do not cross.
 * Returns the parametric position `t` along p1->p2 as well, so callers can pick
 * the crossing nearest one end.
 */
export function segmentIntersection(
  p1: Vec,
  p2: Vec,
  p3: Vec,
  p4: Vec,
): { point: Vec; t: number } | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (denom === 0) return null; // parallel or degenerate
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { point: { x: p1.x + t * d1x, y: p1.y + t * d1y }, t };
}
